import { Button, Form, InputNumber, Select, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import { useUpdateLeaveProrateMutation } from "../../../features/leaveProrate/leaveProrateApi";
import { useGetLeaveTypeListQuery } from "../../../features/leaveType/leaveTypeApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import {
  GetEmployeeProrateListResponseType,
  ModalComponentProps,
} from "../../../types";
import { selectArrayOption, selectSearchOption } from "../../../utils/utils";
import CustomModal from "../../ui/CustomModal";

export default function LeaveProrateUpdate({
  handleClose,
  type,
  data,
  ...rest
}: ModalComponentProps<
  GetEmployeeProrateListResponseType["data"]["prorate_list"][0]
>) {
  const [form] = Form.useForm();
  const {
    data: leaveTypeListData,
    isError: isLeaveTypeError,
    error: leaveTypeError,
  } = useGetLeaveTypeListQuery({
    params: {
      is_published: 1,
    },
  });
  const [updateLeaveProrate, { isLoading, isError, isSuccess, error }] =
    useUpdateLeaveProrateMutation();

  // error toast message
  useError(isLeaveTypeError, leaveTypeError, "Get leave type data fail");
  useError(isError, error, "Leave prorate update fail");

  const onFinish = (values: unknown) => {
    if (data) {
      updateLeaveProrate({
        id: data?.id,
        body: values,
      });
    }
  };

  useEffect(() => {
    if (Object.keys(data || {}) && type === "UPDATE") {
      form.setFieldsValue({
        leave_type_id: data?.leave_type?.id,
        total_days: data?.total_days,
        note: data?.note,
      });
    }
  }, [form, data, type]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Leave prorate has been updated successfully");
      return handleClose();
    }
  }, [handleClose, isSuccess]);

  return (
    <CustomModal
      title="Update Leave Prorate"
      {...rest}
      handleClose={handleClose}
      width={460}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="leave_type_id"
          label="Leave Type"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please select leave type",
            },
          ]}
        >
          <Select
            placeholder="Select"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={selectArrayOption(
              leaveTypeListData?.data.leave_types,
              "title",
              "id"
            )}
            suffixIcon={<SelectDropdownLgIcon />}
          />
        </Form.Item>
        <Form.Item
          name="total_days"
          label="Total Days"
          className="mb-4"
          validateFirst
          rules={[
            {
              required: true,
              message: "Please enter total days",
            },
            {
              validator: (_, value) => {
                if (value < 0) {
                  return Promise.reject(
                    new Error("Leave balance cannot be negative")
                  );
                }
                if (value === 0) {
                  return Promise.reject(
                    new Error("Total days must be at least 1")
                  );
                }
                if (!Number.isInteger(value)) {
                  return Promise.reject(
                    new Error("Total days must be a whole number")
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            size="large"
            placeholder="Ex: 1"
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item name="note" label="Note" className="mb-4">
          <TextArea />
        </Form.Item>

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading}
          >
            Update
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
